import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const LadyBirdDeedVenice = () => {
  return (
    <>
      <Helmet>
        <title>Lady Bird Deed Attorney Venice FL — Protect Your Home | Spanski Law</title>
        <meta name="description" content="Venice FL Lady Bird Deed attorney. Avoid probate, protect your home without a trust. Local office in Venice. Call 941-206-2223 for free consultation." />
        <link rel="canonical" href="https://thelawyerlady.com/lady-bird-deed-venice-fl" />
        <meta property="og:title" content="Lady Bird Deed Attorney Venice FL — Protect Your Home | Spanski Law" />
        <meta property="og:description" content="Venice FL Lady Bird Deed attorney. Avoid probate, protect your home without a trust. Local office in Venice." />
        <meta property="og:url" content="https://thelawyerlady.com/lady-bird-deed-venice-fl" />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thelawyerlady.com" },
            { "@type": "ListItem", "position": 2, "name": "Lady Bird Deed Florida", "item": "https://thelawyerlady.com/lady-bird-deed-florida" },
            { "@type": "ListItem", "position": 3, "name": "Venice", "item": "https://thelawyerlady.com/lady-bird-deed-venice-fl" }
          ]
        })}
        </script>
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Attorney",
          "name": "Season A. Spanski",
          "alternateName": "Spanski Law, PLLC",
          "url": "https://thelawyerlady.com",
          "telephone": "+1-941-206-2223",
          "email": "contact@thelawyerlady.com",
          "address": { "@type": "PostalAddress", "addressLocality": "Venice", "addressRegion": "FL", "addressCountry": "US" },
          "areaServed": [{ "@type": "City", "name": "Venice" }, { "@type": "City", "name": "Nokomis" }, { "@type": "City", "name": "Englewood" }],
          "priceRange": "$800-$1000"
        })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-[#faf9f7]/60">
            <Link to="/" className="hover:text-[#c9a87c] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/lady-bird-deed-florida" className="hover:text-[#c9a87c] transition-colors">Lady Bird Deed Florida</Link>
            <span>/</span>
            <span className="text-[#c9a87c]">Venice</span>
          </nav>
        </div>

        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="font-serif text-4xl md:text-5xl text-[#faf9f7] mb-6">
            Lady Bird Deed Attorney Venice FL — Protect Your Home Without a Trust
          </h1>
          <p className="font-sans text-xl text-[#faf9f7]/70 mb-8 leading-relaxed">
            I'm Season Spanski, and Venice is home. My law office is here, and I help Venice homeowners protect their property using Lady Bird Deeds — a simple, affordable alternative to a trust.
          </p>
          
          <div className="flex flex-wrap gap-4 mb-8">
            <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
              <Phone className="w-5 h-5" />
              Call 941-206-2223
            </a>
            <Link to="/#contact" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
              Free Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="flex items-center gap-2 text-sm text-[#faf9f7]/60">
            <MapPin className="w-4 h-4 text-[#c9a87c]" />
            <span>Venice, FL — Local Office</span>
          </div>
        </section>

        {/* Main Content */}
        <article className="max-w-4xl mx-auto px-6 py-12">
          <div className="prose prose-lg prose-invert max-w-none">
            
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6">Your Local Venice Lady Bird Deed Attorney</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              If you live in Venice, Nokomis, Englewood, or the surrounding area, I'm your neighbor. My office is right here in Venice, and I understand what matters to homeowners in our community: keeping things simple, protecting your family, and not paying more than necessary.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              A Lady Bird Deed does exactly that. It lets you pass your home to your children or other beneficiaries without going through probate — and without the complexity and cost of setting up a trust. You keep full control of your property for as long as you live.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Why Venice Homeowners Don't Always Need a Trust</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Many estate planning attorneys push trusts on every client, but the truth is that a trust isn't always necessary. If your primary goal is to pass your home to your kids without probate, a Lady Bird Deed often does the job just as well — for a fraction of the cost.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              A trust might cost $2,000-$5,000 or more. A Lady Bird Deed costs $800-$1,000. Both avoid probate for your home. The difference is that a trust can handle other assets and more complex situations, while a Lady Bird Deed is specifically designed for Florida real estate.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I'll be honest with you about which option makes sense for your situation. Sometimes a trust really is the better choice. But for many Venice homeowners, a Lady Bird Deed is all you need.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">What to Expect When We Work Together</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Because I'm local, I'm happy to meet with you in person at my Venice office. We'll talk about your property, who you want to inherit it, and any concerns you have. I'll draft your Lady Bird Deed, you'll review it, and then we'll handle signing and notarization — either at my office or using remote online notarization if you prefer.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I record the deed with the Sarasota County Clerk of Court (Venice is in Sarasota County), and you'll have a recorded copy for your files. The whole process usually takes 1-2 weeks.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Serving Venice and Beyond</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              While Venice is home, I serve homeowners throughout Florida. If you have property in <Link to="/lady-bird-deed-sarasota" className="text-[#c9a87c] hover:underline">Sarasota</Link>, <Link to="/lady-bird-deed-tampa-fl" className="text-[#c9a87c] hover:underline">Tampa</Link>, <Link to="/lady-bird-deed-orlando-fl" className="text-[#c9a87c] hover:underline">Orlando</Link>, or anywhere else in the state, I can help with that too. Learn more about my <Link to="/lady-bird-deed-florida" className="text-[#c9a87c] hover:underline">statewide Lady Bird Deed services</Link>.
            </p>

          </div>
        </article>

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#c9a87c]/20 rounded-lg p-8 md:p-12 text-center">
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-4">Let's Talk About Your Home</h2>
            <p className="text-[#faf9f7]/70 mb-8 max-w-2xl mx-auto">
              Call 941-206-2223 or schedule a free consultation online. As your local Venice attorney, I'm here to help you protect what matters most.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
                <Phone className="w-5 h-5" />
                Call 941-206-2223
              </a>
              <Link to="/#contact" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
                Schedule Online
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LadyBirdDeedVenice;
